import { Shield, TrendingUp, AlertTriangle, BarChart3, Clock } from "lucide-react";

const RAG_STYLES = {
  green: { bg: "bg-emerald-500/10", border: "border-emerald-500/20", text: "text-emerald-400", dot: "bg-emerald-500" },
  amber: { bg: "bg-amber-500/10", border: "border-amber-500/20", text: "text-amber-400", dot: "bg-amber-500" },
  red: { bg: "bg-rose-500/10", border: "border-rose-500/20", text: "text-rose-400", dot: "bg-rose-500" },
  gray: { bg: "bg-slate-800", border: "border-slate-700", text: "text-slate-500", dot: "bg-slate-600" },
};

const FACTOR_META = {
  confidence: { label: "Confidence Level", icon: TrendingUp, descByScore: ["Good pricing basis", "Some uncertainty in pricing basis", "Significant pricing risk"] },
  discount: { label: "Discount Applied", icon: BarChart3, descByScore: ["Within acceptable range", "Moderate discount applied", "High discount - review recommended"] },
  risk_hours: { label: "Risk Hours", icon: AlertTriangle, descByScore: ["Risk exposure is low", "Moderate risk hours added", "High risk exposure"] },
  benchmark: { label: "Benchmark Comparison", icon: Clock, descByScore: ["Fee aligns with benchmark", "Fee slightly outside target", "Fee outside benchmark range"] },
};

const SCORE_COLORS = ["text-emerald-400", "text-amber-400", "text-rose-400"];
const SCORE_BG = ["bg-emerald-500/10", "bg-amber-500/10", "bg-rose-500/10"];

export function HealthScoreBadge({ healthScore, size = "default" }) {
  if (!healthScore || healthScore.color === "gray") return null;

  const style = RAG_STYLES[healthScore.color] || RAG_STYLES.gray;
  const breakdown = healthScore.breakdown || {};
  const hasBreakdown = Object.keys(breakdown).length > 0;

  if (size === "dot") {
    return (
      <span className={`w-2.5 h-2.5 rounded-full ${style.dot} cursor-help`} data-testid="health-score-dot" title={healthScore.label} />
    );
  }

  if (size === "compact") {
    return (
      <div className="relative inline-flex" data-testid="health-score-compact-wrapper">
        <span className={`health-score-trigger inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold border cursor-help ${style.bg} ${style.border} ${style.text}`} data-testid="health-score-compact">
          <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
          {healthScore.label}
        </span>
        {hasBreakdown && <TooltipPanel healthScore={healthScore} breakdown={breakdown} />}
      </div>
    );
  }

  return (
    <div className="relative inline-block" data-testid="health-score-wrapper">
      <div className={`health-score-trigger inline-flex items-center gap-2 px-3 py-2 rounded-lg border cursor-help ${style.bg} ${style.border}`} data-testid="health-score-badge">
        <Shield className={`w-4 h-4 ${style.text}`} strokeWidth={1.5} />
        <div>
          <div className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${style.dot}`} />
            <span className={`text-sm font-semibold ${style.text}`}>{healthScore.label}</span>
          </div>
          <p className="text-[10px] text-slate-500 mt-0.5">Pricing Health Score</p>
        </div>
      </div>
      {hasBreakdown && <TooltipPanel healthScore={healthScore} breakdown={breakdown} />}
    </div>
  );
}

function TooltipPanel({ healthScore, breakdown }) {
  const overallStyle = RAG_STYLES[healthScore.color] || RAG_STYLES.gray;
  return (
    <div className="health-tooltip absolute left-0 top-full mt-2 z-[99999] opacity-0 pointer-events-none" data-testid="health-score-tooltip">
      <div className="health-tooltip-inner bg-slate-800 border border-slate-700 rounded-xl shadow-2xl p-4 text-xs w-72">
        {/* Arrow */}
        <div className="absolute -top-1.5 left-8 w-3 h-3 bg-slate-800 border-l border-t border-slate-700 rotate-45" />
        {/* Header */}
        <div className="flex items-center gap-2 mb-3 pb-2.5 border-b border-slate-700">
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${overallStyle.bg}`}>
            <Shield className={`w-3.5 h-3.5 ${overallStyle.text}`} />
          </div>
          <div>
            <p className={`font-bold text-sm ${overallStyle.text}`}>{healthScore.label}</p>
            <p className="text-[10px] text-slate-500">Pricing Health Score</p>
          </div>
        </div>
        {/* Factor breakdown */}
        <div className="space-y-2">
          {Object.entries(breakdown).map(([key, factor]) => {
            const meta = FACTOR_META[key] || { label: key, descByScore: ["OK", "Warning", "Concern"] };
            const Icon = meta.icon || TrendingUp;
            const scoreIdx = Math.min(factor.score || 0, 2);
            return (
              <div key={key} className={`flex items-start gap-2.5 p-2 rounded-lg ${SCORE_BG[scoreIdx]}`}>
                <Icon className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${SCORE_COLORS[scoreIdx]}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-slate-200 text-[11px]">{meta.label}</span>
                    <span className={`font-mono font-bold text-[11px] ${SCORE_COLORS[scoreIdx]}`}>{factor.label}</span>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-0.5 leading-relaxed">{meta.descByScore[scoreIdx]}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
